import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { LoadingState, ErrorState } from "../components/states";
import { SignedOutView } from "./SignedOutView";
import { NotEligibleNotice } from "./NotEligibleNotice";
import { MyConsumptionPage } from "./MyConsumptionPage";
import { useLandingCheck } from "../hooks/useLandingCheck";

const RequestDecidePermission = "request.approve";

/**
 * The root route. Renders the signed-out, unclassified (`UR-V-01`) and signed-in states
 * distinctly, as the phase 4 brief requires, plus a loading state while the first
 * `GET /api/session/me` call is in flight and an error state if it fails outright (`QR-07`).
 */
export function HomeRoute(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot, refresh } = useSession();

  switch (snapshot.status) {
    case "loading":
      return <LoadingState />;
    case "error":
      return (
        <ErrorState
          title={t("sessionError.title")}
          description={t("sessionError.description")}
          onRetry={() => void refresh()}
        />
      );
    case "signed-out":
      return <SignedOutView />;
    case "unclassified":
      return <NotEligibleNotice person={snapshot.person} classificationState={snapshot.classificationState} />;
    case "signed-in":
      return <SignedInHome canDecideRequests={snapshot.permissions.includes(RequestDecidePermission)} />;
  }
}

/**
 * The signed-in root: My Usage, except that an approver with requests waiting for their decision is sent to My Requests when the
 * app opens, so what needs them comes first.
 */
function SignedInHome({ canDecideRequests }: { canDecideRequests: boolean }): React.JSX.Element {
  const ready = useLandingCheck(canDecideRequests);
  return ready ? <MyConsumptionPage /> : <LoadingState />;
}
